import {computed, effect, inject, Injectable, signal, untracked} from '@angular/core';
import {CancellationToken, CaptureError, debounce, IMessageCode, JsonValue} from "@mindpath/shared";
import * as Sentry from "@sentry/angular";
import {config} from "../../config";
import {AuthService} from "../auth";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {apiUrl} from "../networking";
import {AppReadyChecker} from "../app.init";


@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  #httpClient = inject(HttpClient);
  #appReady = inject(AppReadyChecker);

  readonly #bufferedEvents$ = signal<ReadonlySet<Analytics.Event>>(new Set());
  readonly #savedEvents$ = signal<ReadonlySet<Analytics.Event>>(new Set());

  #initialized = false;

  readonly events = computed(() => {
    return [...this.#bufferedEvents$(), ...this.#savedEvents$()].toSorted((a, b) => a.timestamp - b.timestamp);
  });

  readonly #authData = inject(AuthService.Data);

  readonly #canSendAnalytics$ = computed(() => {
    return !!this.#authData()?.session;
  });

  readonly sessionId = computed(() => {
    return this.#authData()?.session?.id ?? EmptyGuid;
  });

  readonly userId = computed(() => {
    return this.#authData()?.user?.id ?? EmptyGuid;
  });


  reset() {
    this.#bufferedEvents$.set(new Set());
    this.#savedEvents$.set(new Set());
    this.#fieldChangeBuffer.clear();
    sessionStorage.removeItem('analytics:pageFields');
  }

  #resetWaiter = (() => {
    let waiterInterval: number | undefined;
    return () => {
      if (waiterInterval) {
        clearInterval(waiterInterval);
      }
      waiterInterval = setInterval(() => {
        if (!this.#bufferedEvents$().size) {
          return;
        }
        this.flush();
      }, 1000) as unknown as number;
    }
  })()

  constructor() {

    effect(() => {
      const canSend = this.#canSendAnalytics$();
      const bufferedEvents = this.#bufferedEvents$();
      if (!canSend) {
        return;
      }
      if (bufferedEvents.size) {
        untracked(() => {
          return this.flush();
        })
      }
    });
  }

  init() {
    if (this.#initialized) {
      return Promise.resolve();
    }
    this.#initialized = true;

    // load the page field cache from the sessionStorage so as to not send duplicate events on page load
    try {
      this.#loadFieldBuffer();
    } catch (e) {
      console.error('Error loading field buffer', e);
      Sentry.captureException(e);
    }
    return Promise.resolve();
  }

  clearEvents() {
    this.#bufferedEvents$.set(new Set());
    this.#savedEvents$.set(new Set());
  }

  readonly flush = debounce(() => {
    return this.#immediateFlush();
  }, 350);


  #flushPromise: Promise<void> | undefined;

  async #immediateFlush(cancel: CancellationToken = CancellationToken.None) {

    if (!this.#appReady()) {
      return;
    }

    // if analytics can't be sent (no user), don't send
    if (!this.#canSendAnalytics$()) {
      if (!config.isProduction) {
        console.warn('Analytics not enabled');
      }
      return;
    }

    // Deliberately `startInactiveSpan` rather than `startSpan`: `startSpan` keeps its scope on the
    // stack until the returned promise settles, and the browser has no async-context isolation, so
    // every HttpClient request issued while a flush was in flight became a child of the flush span.
    // Since successful flushes are now dropped in `beforeSendTransaction`, that would silently take
    // real API spans with them. `startInactiveSpan` pushes no scope (trace.js:154-160).
    //
    // `startNewTrace` is gone for the same reason — it also pushed a scope, leaking its new trace id
    // across the POST, so unrelated errors captured mid-flush were filed under the flush's trace.
    const span = Sentry.startInactiveSpan({
      name: 'Flush Analytics',
      op: 'analytics.flush',
      // One shape to reason about in `beforeSendTransaction`, whether or not a pageload span is active.
      forceTransaction: true,
    });

    // `#flushPromise` sequences flushes; it is not a result. A rejected parent must not reject its
    // children, or a single failure poisons every flush that follows it.
    const parentPromise: Promise<void> = this.#flushPromise?.catch(() => undefined) ?? Promise.resolve();

    const mine: Promise<void> = parentPromise.then(async () => {
      const bufferedEvents = this.#bufferedEvents$();
      const eventsToSend = Array.from(bufferedEvents)
        .sort((a, b) => a.timestamp - b.timestamp).map(e => {
          return {
            ...e,
          };
        });

      if (!eventsToSend.length) {
        return;
      }

      try {
        await lastValueFrom(this.#httpClient.post(apiUrl`${config}/events`, eventsToSend).pipe(cancel.asTakeUntil()));
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
          return;
        }
        span.setStatus({
          code: 2, // 2 = ERROR
        });
        console.error('Error sending events', e);
        Sentry.captureException(e);
      }

      try {
        this.#bufferedEvents$.update(events => {
          return events.difference(bufferedEvents);
        });
        this.#savedEvents$.update(events => {
          return new Set([...events, ...eventsToSend]);
        });

        if (eventsToSend.some(e => e.type === Analytics.EventType.Field && e.action === 'change')) {
          this.#saveFieldBuffer();
        }
      } catch (e) {
        span.setStatus({
          code: 2, // 2 = ERROR
        });
        console.error('Error moving buffered events to saved events', e);
        Sentry.captureException(e);
      }
    });

    this.#flushPromise = mine;

    try {
      await mine;
    } catch (e) {
      // Nothing above rethrows, so reaching here means the chain itself broke unexpectedly.
      span.setStatus({
        code: 2, // 2 = ERROR
      });
      console.error('Error flushing events', e);
      Sentry.captureException(e);
    } finally {
      // Only clear our own handle. A flush that started while this one was in flight has already
      // installed its own; clobbering it would leave the next flush with no parent to wait on, so it
      // would run concurrently, read the same buffer before the swap, and POST the same events twice.
      if (this.#flushPromise === mine) {
        this.#flushPromise = undefined;
      }
      span.end();
    }
  }

  #saveFieldBuffer() {
    const data = Object.fromEntries(this.#fieldChangeBuffer.entries());
    sessionStorage.setItem('analytics:pageFields', JSON.stringify(data));
  }

  #loadFieldBuffer() {
    const pageFields = sessionStorage.getItem('analytics:pageFields');
    if (!pageFields) {
      return;
    }
    const fields = JSON.parse(pageFields) as Record<string, [Analytics.Id, string | undefined | null | number | boolean]>;
    for (const field of Object.keys(fields)) {
      const [id, value] = fields[field];
      this.#fieldChangeBuffer.set(field, [id, value]);
    }
  }

  #fieldChangeBuffer = new Map<string, [Analytics.Id, string | undefined | null | number | boolean]>();

  #checkFieldChange(field: string, value: string | undefined | null | number | boolean, id: Analytics.Id) {

    const [previousId, previousValue] = this.#fieldChangeBuffer.get(field) ?? [];
    // the type of the value has changed, so we need to send the event
    if (typeof previousValue !== typeof value) {
      this.#fieldChangeBuffer.set(field, [id, value]);
      return true;
    }

    // both are falsy, so we don't need to send the event (we alredy checked for different falsy values above based on type)
    if (!previousValue && !value) {
      return false;
    }

    // if the value has not changed, don't send the event
    if (previousValue === value) {
      return false;
    }
    if (!previousValue) {
      this.#fieldChangeBuffer.set(field, [id, value]);
      return true
    }

    if (typeof previousValue === 'number' || typeof value === 'number' || typeof previousValue === 'boolean' || typeof value === 'boolean') {
      this.#fieldChangeBuffer.set(field, [id, value]);
    } else if (typeof previousValue === 'string' || typeof value === 'string') {

      // if the value is longer than the previous value
      // and starts with the previous value
      // and it has not been sent
      // then remove the previous event from the buffer
      if (value && previousValue.length < value.length && value.startsWith(previousValue) &&
        !Array.from(this.#savedEvents$()).find(e => e.id === previousId)) {
        // if we have not sent the previous event, remove it from the buffer
        this.#bufferedEvents$.update(events => {
          const previousEvent = Array.from(events).find(e => e.id === previousId);
          return events.difference(new Set([previousEvent]));
        });
      }
    }
    this.#fieldChangeBuffer.set(field, [id, value]);
    return true;
  }

  fireFieldChange(page: string, specifier: string, value: JsonValue, opt?: { skipCheck: boolean }): void {

    const id = crypto.randomUUID();
    if ((typeof value !== 'string' && typeof value !== 'boolean' && typeof value !== 'number') && value !== null && value !== undefined || opt?.skipCheck) {
      return this.fireEvent(page, Analytics.EventType.Field, {action: 'change', specifier, data: value}, {
        force: false,
        id
      });
    }
    if (this.#checkFieldChange(specifier, value, id)) {
      return this.fireEvent(page, Analytics.EventType.Field, {action: 'change', specifier, data: value}, {
        force: false,
        id
      });
    }
  }

  fireEvent<T extends Analytics.EventType>(page: string, type: T, action: Analytics.EventActions[T], opt: {
    force: true,
    id?: Analytics.Id
  }): Promise<void>;
  fireEvent<T extends Analytics.EventType>(page: string, type: T, action: Analytics.EventActions[T], opt?: {
    force?: false | undefined,
    id?: Analytics.Id
  }): void;
  fireEvent<T extends Analytics.EventType>(page: string, type: T, action: Analytics.EventActions[T], opt?: {
    force?: boolean,
    id?: Analytics.Id
  }) {

    try {
      this.#resetWaiter();
      const event: Analytics.Event<T> = {
        id: opt?.id ?? crypto.randomUUID(),
        url: location.href,
        type,
        sessionId: this.sessionId(),
        timestamp: Date.now(),
        page,
        ...action,
      };

      const force = opt?.force || (type === Analytics.EventType.Page && action.action === 'exit');
      this.#bufferedEvents$.update(events => events.union(new Set([event])));

      if (force) {
        return this.#immediateFlush();
      }
      return;
    } catch (e) {
      CaptureError(e, {details: 'Error applying event',});
      return;
    }

  }
}


const EmptyGuid = '00000000-0000-0000-0000-000000000000' as const;

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Analytics {
  export type Id = `${string}-${string}-${string}-${string}`;

  export type Event<T extends EventType = EventType> = EventActions[T] & {
    readonly id: Id;
    readonly type: T;
    readonly page: string;
    readonly sessionId: Id;
    readonly url: string;
    readonly timestamp: number;
  }

  export enum EventType {
    Page = 'page',
    Button = 'button',
    Field = 'field',
    Error = 'error',
    Fetch = 'fetch',
    ResultPaging = 'resultPaging',
    Custom = 'custom',
    ScopedTo = 'scopedTo',
  }

  interface EventPayload<TName extends string = string, TValue extends JsonValue = JsonValue> {
    readonly action: TName;
    readonly specifier?: string;
    readonly data: TValue;
  }

  export interface EventActions {
    [EventType.Page]: EventPayload<'state', 'enter' | 'exit' | 'focus' | 'blur' | 'scroll'>;
    [EventType.Button]: EventPayload<'click' | 'focus' | 'blur' | 'hover'>;
    [EventType.Field]: EventPayload<'error' | 'change'> & { specifier: string };
    [EventType.Error]: EventPayload<'code', IMessageCode['code']> | EventPayload<'stack'>;
    [EventType.Fetch]: EventPayload;
    [EventType.ResultPaging]: EventPayload<'next' | 'previous' | 'goto' | 'first' | 'last', number>;
    [EventType.Custom]: EventPayload;
    [EventType.ScopedTo]: EventPayload<'scoped' | 'unscoped'>;
  }
}
